import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useHistoryDismiss from '../../hooks/common/useHistoryDismiss';

const DISMISS_ONLY_LABELS = new Set(['閉じる', 'キャンセル', 'いいえ', 'Cancel']);

// 入れ子モーダル（モーダルの中から開くモーダル）の層スタック。useHistoryDismiss と同型のLIFO。
//   - 後から開いた層が常に前面（overlay の data-layer → CSS の calc(var(--z-modal) + N)）。
//   - Esc とフォーカストラップは最前面の層だけが処理する（背面のモーダルまで一緒に閉じない）。
// 生の z-index を画面側で足さないための受け皿。§2.7 / §2.8（docs/DEVELOPMENT.md）。
const layerStack = [];
const MAX_LAYER_OFFSET = 3;

function normalizeFooterText(value) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function extractFooterText(node) {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractFooterText).join('');
  if (React.isValidElement(node)) return extractFooterText(node.props?.children);
  return '';
}

function isEmptyFooterNode(node) {
  if (node == null || typeof node === 'boolean') return true;
  if (Array.isArray(node)) return node.every(isEmptyFooterNode);
  return false;
}

function stripDismissOnlyFooter(node, shouldStrip) {
  if (!shouldStrip || node == null || typeof node === 'boolean') return node;
  if (Array.isArray(node)) {
    const children = node
      .map((child) => stripDismissOnlyFooter(child, shouldStrip))
      .filter((child) => !isEmptyFooterNode(child));
    return children.length ? children : null;
  }
  if (!React.isValidElement(node)) return node;

  const label = normalizeFooterText(extractFooterText(node));
  if (DISMISS_ONLY_LABELS.has(label)) return null;

  const children = node.props?.children;
  if (children == null) return node;
  const nextChildren = stripDismissOnlyFooter(children, shouldStrip);
  if (isEmptyFooterNode(nextChildren)) return null;
  if (nextChildren === children) return node;
  return React.cloneElement(node, undefined, nextChildren);
}

/**
 * DsModal — モーダルの正規実装（Web）。
 *
 * 画面ごとに overlay/card/close を作り直さない受け皿。挙動（Esc・背面クリックで閉じる・
 * フォーカストラップ・背面スクロールロック・body へ portal・aria）を1か所に集約する。
 * 見た目は `.ds-modal*`（components.css・トークン）を継承する。
 *
 * 入れ子（モーダルの中から開くモーダル）もこの部品で表現する。開いた順に層が積まれ、
 * 後から開いた方が必ず前面に出て、Esc・戻る・フォーカストラップは最前面だけが処理する。
 * 画面側で `position: fixed` の独自 overlay を作ると、portal 済みの DsModal より下に潜る。
 *
 * props:
 *   open: boolean
 *   onClose: () => void          Esc / 背面クリック / ✕ / footer から呼ぶ
 *   title?: ReactNode            ヘッダー見出し（aria-labelledby に連動）
 *   ariaLabel?: string           title 無しのときのアクセシブル名
 *   footer?: ReactNode           操作ボタン列（省略でフッター非表示）
 *   size?: 'sm' | 'md'(既定) | 'lg'
 *   dismissible?: boolean=true   false で Esc・背面クリック・✕ を無効（処理中の誤操作防止）
 *   showClose?: boolean=true     ヘッダー右の ✕ を出すか
 *   className?: string           card への追加クラス
 *   overlayClassName?: string    overlay への追加クラス
 *   bodyClassName?: string       body への追加クラス
 *   children: ReactNode          本文（ここだけがスクロールする）
 *
 * 正は docs/DEVELOPMENT.md（§2.8 部品ファースト / §2.5 早見表）。
 */
export default function DsModal({
  open,
  onClose,
  title,
  ariaLabel,
  footer,
  size = 'md',
  dismissible = true,
  showClose = true,
  closeOnBack = true,
  className = '',
  overlayClassName = '',
  bodyClassName = '',
  children,
  ...rest
}) {
  const cardRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  const layerTokenRef = useRef(null);
  const [layerOffset, setLayerOffset] = useState(0);
  const titleId = useId();

  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;

  // 開いた順に層を積む。paint 前に確定させたいので layout effect（前面/背面が1フレーム入れ替わらない）。
  useLayoutEffect(() => {
    if (!open) return undefined;
    const token = {};
    layerTokenRef.current = token;
    layerStack.push(token);
    setLayerOffset(Math.min(layerStack.length - 1, MAX_LAYER_OFFSET));
    return () => {
      const index = layerStack.lastIndexOf(token);
      if (index !== -1) layerStack.splice(index, 1);
      layerTokenRef.current = null;
      setLayerOffset(0);
    };
  }, [open]);

  // 最前面の層か（背面の層は Esc もフォーカストラップも処理しない）。
  const isTopLayer = useCallback(() => {
    const token = layerTokenRef.current;
    if (!token) return true;
    return layerStack[layerStack.length - 1] === token;
  }, []);

  const requestClose = useCallback(() => {
    if (dismissibleRef.current) onCloseRef.current?.();
  }, []);

  // ブラウザの戻る/戻るジェスチャーで閉じる（ページ遷移の前にモーダルを閉じる）。
  // closeOnBack={false} で個別に無効化できる（履歴を自前管理する画面向け）。
  useHistoryDismiss(open && dismissible && closeOnBack, requestClose);

  useEffect(() => {
    if (
      open &&
      process.env.NODE_ENV !== 'production' &&
      showClose &&
      title == null &&
      ariaLabel != null
    ) {
      // Header title is the canonical modal title. ariaLabel-only modals with
      // a close button tend to create an empty first row and duplicate body title.
      // eslint-disable-next-line no-console
      console.warn(
        'DsModal: pass title when showClose is true. Use showClose={false} only for custom presentation modals.',
      );
    }
  }, [ariaLabel, open, showClose, title]);

  useEffect(() => {
    if (!open) return undefined;
    const card = cardRef.current;
    const prevActive = document.activeElement;

    const focusables = () =>
      card
        ? Array.prototype.filter.call(
            card.querySelectorAll(
              'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ),
            (el) => el.offsetParent !== null,
          )
        : [];

    // 開いたら最初のフォーカス可能要素（無ければカード）へ移す
    const items = focusables();
    (items[0] || card)?.focus();

    const onKeyDown = (e) => {
      if (!isTopLayer()) return; // 背面の層は前面モーダルの Esc / Tab を横取りしない。
      if (e.key === 'Escape' && dismissibleRef.current) {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const f = focusables();
      if (!f.length) {
        e.preventDefault();
        card?.focus();
        return;
      }
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden'; // 背面スクロールロック

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      if (prevActive && typeof prevActive.focus === 'function') prevActive.focus();
    };
  }, [isTopLayer, open]);

  if (!open) return null;

  const renderedFooter = stripDismissOnlyFooter(footer, showClose && dismissible);

  return createPortal(
    <div
      className={`ds-modal-overlay${overlayClassName ? ` ${overlayClassName}` : ''}`}
      data-layer={layerOffset > 0 ? layerOffset : undefined}
      role="presentation"
      onMouseDown={requestClose}
    >
      <div
        ref={cardRef}
        className={`ds-modal ds-modal--${size}${className ? ` ${className}` : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title != null ? titleId : undefined}
        aria-label={title == null ? ariaLabel : undefined}
        tabIndex={-1}
        onMouseDown={(e) => e.stopPropagation()}
        {...rest}
      >
        {(title != null || showClose) && (
          <div className="ds-modal__header">
            {title != null ? (
              <h2 id={titleId} className="ds-modal__title">
                {title}
              </h2>
            ) : (
              <span />
            )}
            {showClose && (
              <button
                type="button"
                className="ds-modal__close"
                onClick={requestClose}
                aria-label="閉じる"
              >
                <i className="fa-solid fa-xmark" aria-hidden="true" />
              </button>
            )}
          </div>
        )}
        <div className={`ds-modal__body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>
        {renderedFooter != null && <div className="ds-modal__footer">{renderedFooter}</div>}
      </div>
    </div>,
    document.body,
  );
}
